import React from "react";
import {
  Grid,
  Button,
  Typography,
  Paper,
  Stack,
  SelectChangeEvent,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import { useDispatchWrapper } from "../../../hooks";
import {
  CustomFormLabel,
  ControlledCustomSelect,
} from "../../../components/formsComponents";
import {
  CustomDatePicker,
  CustomTimePicker,
} from "../../../components/formsComponents/CustomDatePicker";
import {
  upsertMarketCampaignCronSchedulerAsync,
  fetchMarketCampaignCronSchedulersAsync,
  upsertMarketCampaignBasicInfoAsync,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { useFormik } from "formik";
import { NavigateBefore } from "@mui/icons-material";
import produce from "immer";
import { capitalizeWords } from "../../../helpers";
import { useBillingCompanyContext } from "../../../providers";
import { TabsList } from "../../../components/Tabs/TabsList";
import { useMarketCampaignContext } from "../MarketCampaign.context-state";
import { useReduxStore } from "../../../redux/store.hooks";

interface IStep4ScheduleProps {
  onFinish: () => void;
}

const daysOfWeek = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const Step4MarketCampaignSchedule: React.FC<IStep4ScheduleProps> = ({
  onFinish,
}) => {
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const { marketCampaign, isEditable, onKeyChange, draftSteps } =
    useMarketCampaignContext();

  const { data } = useReduxStore("marketCampaign").marketCampaignCronScheduler;

  const [tabValue, setTabValue] = React.useState(0);

  const { values, setValues, setFieldValue } = useFormik({
    initialValues: {
      ...data,
      cron_scheduler: {
        minutes: "0",
        seconds: "0",
        hours: "12",
        end_date: "",
        start_date: "",
        repeatEvery: false,
        repeatType: "DAILY",
        week: "",
        ...data?.cron_scheduler,
      },
    },
    onSubmit: (values) => {
      dispatch(
        upsertMarketCampaignCronSchedulerAsync(values, () => {
          onFinish();
        }),
      );
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset values when switching tabs
    const newValues = produce(values, (draft) => {
      draft.cron_scheduler = {
        minutes: "0",
        seconds: "0",
        hours: "12",
        end_date: "",
        start_date: "",
        repeatEvery: newValue === 1, // true for Repeat tab, false for Once tab
        repeatType: "DAILY",
        week: "",
      };
    });
    setValues(newValues);
  };

  const handleWeekChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string[];
    const weekString = value.join(",");
    setFieldValue("cron_scheduler.week", weekString);
  };

  const selectedWeeks = values.cron_scheduler?.week
    ? values.cron_scheduler.week.split(",").filter(Boolean)
    : [];

  const handleDateChange = (field: string) => (date: any | null) => {
    const newDate = new Date(date);
    setFieldValue(`cron_scheduler.${field}`, date ? newDate.toISOString() : "");

    // For "Schedule Once" tab, set both start and end date to the same value
    if (tabValue === 0 && field === "start_date") {
      setFieldValue(
        "cron_scheduler.end_date",
        date ? newDate.toISOString() : "",
      );
    }
  };

  const handleSaveAndFinish = () => {
    // Create the minimal payload structure
    const basePayload = {
      market_campaign_uuid: marketCampaign.market_campaign_uuid,
      company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      cron_scheduler: {
        start_date: values.cron_scheduler?.start_date || "",
        end_date: values.cron_scheduler?.end_date || "",
        seconds: values.cron_scheduler?.seconds || "",
        // minutes: values.cron_scheduler?.minutes || "",
        minutes: values.cron_scheduler?.minutes
          ? values.cron_scheduler.minutes.replace(/^0+/, "") || "0"
          : "0",
        hours: values.cron_scheduler?.hours || "",
        repeatEvery: tabValue === 1, // true for Repeat tab, false for Once tab
      },
      status: "ACTIVE",
    };

    // Only add week field if repeat type is WEEKLY
    const payload =
      values.cron_scheduler?.repeatType === "WEEKLY"
        ? {
            ...basePayload,
            cron_scheduler: {
              ...basePayload.cron_scheduler,
              week: values.cron_scheduler?.week || "",
            },
          }
        : basePayload;

    dispatch(
      upsertMarketCampaignBasicInfoAsync(
        {
          ...marketCampaign,
          campaign_start_date: values.cron_scheduler?.start_date,
          campaign_end_date: values.cron_scheduler?.end_date,
        },
        (data) => {
          dispatch(
            upsertMarketCampaignCronSchedulerAsync(payload, () => {
              onFinish();
            }),
          );
        },
      ),
    );

    // dispatch(
    //   upsertMarketCampaignCronSchedulerAsync(payload, () => {
    //     onFinish();
    //   })
    // );
  };

  const handleCancelSchedule = () => {
    dispatch(
      upsertMarketCampaignCronSchedulerAsync(
        { ...values, status: "INACTIVE" },
        () => {
          onFinish();
        },
      ),
    );
  };

  const handleRepeat = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draftValues) => {
      draftValues.cron_scheduler.week = "";
      draftValues.cron_scheduler.repeatType = value;
    });
    setValues(newValues);
  };

  React.useEffect(() => {
    if (!marketCampaign.market_campaign_uuid) return;
    dispatch(
      fetchMarketCampaignCronSchedulersAsync(
        marketCampaign.market_campaign_uuid,
      ),
    );
  }, [marketCampaign.market_campaign_uuid]);

  React.useEffect(() => {
    setValues({
      ...data,
      cron_scheduler: {
        minutes: "0",
        seconds: "0",
        hours: "12",
        end_date: "",
        start_date: "",
        repeatType: "",
        repeatEvery: data?.cron_scheduler?.repeatEvery || false,
        week: "",
        ...data?.cron_scheduler,
      },
    });

    if (data?.cron_scheduler?.repeatEvery) {
      setTabValue(1);
    } else {
      setTabValue(0);
    }
  }, [data]);

  const getAvailableRepeatTypes = (
    startDate: string | null,
    endDate: string | null,
  ): string[] => {
    if (!startDate || !endDate) return [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffInDays =
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    if (diffInDays <= 14) return ["DAILY"];
    if (diffInDays < 60) return ["DAILY", "WEEKLY"];
    return ["DAILY", "WEEKLY", "MONTHLY"];
  };

  const handelBackButtonClick = () => {
    onKeyChange("activeStep", 2);
  };

  return (
    <>
      {marketCampaign.sending_type === "QUICK" && (
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} md={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Stack spacing={2} alignItems="center">
                {/* Success Header */}
                <Typography variant="h5" fontWeight={600} color="success.main">
                  🎉 Campaign Run Successfully
                </Typography>

                <Typography variant="subtitle2" color="text.secondary">
                  Your campaign has been executed successfully with the details
                  below
                </Typography>

                {/* Campaign Name */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {
                      draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.campaign_name
                    }
                  </Typography>
                </Box>

                <Divider sx={{ width: "100%" }} />

                {/* Meta Info */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created By
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.created_by_name || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created On
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.create_ts
                        ? new Date(
                            draftSteps?.draft_step_data?.step_three_data
                              .campaign_info.create_ts,
                          ).toLocaleString()
                        : "-"}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ width: "100%" }} />

                {/* Message Stats */}
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Sent
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.sent_messages || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.failed_messages || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h6">
                      {draftSteps?.draft_step_data?.step_three_data
                        ?.campaign_info?.total_audience || 0}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Dates (optional) */}
                {(draftSteps?.draft_step_data?.step_three_data?.campaign_info
                  ?.campaign_start_date ||
                  draftSteps?.draft_step_data?.step_three_data?.campaign_info
                    ?.campaign_end_date) && (
                  <>
                    <Divider sx={{ width: "100%" }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography variant="body2">
                          {draftSteps?.draft_step_data?.step_three_data
                            ?.campaign_info?.campaign_start_date
                            ? new Date(
                                draftSteps.draft_step_data?.step_three_data
                                  .campaign_info.campaign_start_date,
                              ).toLocaleDateString()
                            : "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography variant="body2">
                          {draftSteps?.draft_step_data?.step_three_data
                            ?.campaign_info?.campaign_end_date
                            ? new Date(
                                draftSteps.draft_step_data?.step_three_data
                                  .campaign_info.campaign_end_date,
                              ).toLocaleDateString()
                            : "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {marketCampaign.sending_type === "SCHEDULING" && (
        <Box sx={{ maxWidth: 500, margin: "auto", p: 3 }}>
          <Box sx={{ borderColor: "divider", position: "relative" }}>
            <TabsList
              sx={{
                mb: 2,
                mr: 2,
                cursor: "pointer",
                ...(!isEditable && {
                  cursor: "not-allowed",

                  opacity: 1,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    // backgroundColor: "rgba(255, 255, 255, 0.23)",
                    zIndex: 1,
                  },
                }),
              }}
              varaint="fullWidth"
              selectedTab={tabValue}
              tabs={[
                { label: "Schedule Once" },
                { label: "Schedule Repeatedly" },
              ]}
              onChange={handleTabChange}
            />
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomFormLabel>Date</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.cron_scheduler?.start_date || null}
                  onChange={handleDateChange("start_date")}
                  disabled={!isEditable}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomFormLabel>Time</CustomFormLabel>
                <CustomTimePicker
                  fullWidth
                  value={
                    values?.cron_scheduler?.hours &&
                    values?.cron_scheduler?.minutes
                      ? `${values.cron_scheduler.hours.padStart(
                          2,
                          "0",
                        )}:${values.cron_scheduler.minutes.padStart(2, "0")}:00`
                      : null
                  }
                  onChange={(timeString: any) => {
                    if (timeString) {
                      const [hours, minutes] = timeString.split(":");
                      setFieldValue("cron_scheduler.hours", hours);
                      // setFieldValue("cron_scheduler.minutes", minutes);
                      setFieldValue(
                        "cron_scheduler.minutes",
                        minutes.toString().replace(/^0+/, "") || "0",
                      );
                      setFieldValue("cron_scheduler.seconds", "0");
                    }
                  }}
                  disabled={!isEditable}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveAndFinish}
                  sx={{
                    mt: 2,
                    ...(!isEditable && {
                      cursor: "not-allowed",
                    }),
                  }}
                  disabled={!isEditable || !values.cron_scheduler?.start_date}
                >
                  Save Schedule
                </Button>
                {marketCampaign.status === "SCHEDULED" && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleCancelSchedule}
                    sx={{
                      mt: 2,
                      ...(!isEditable && {
                        cursor: "not-allowed",
                      }),
                    }}
                    disabled={!isEditable || !values.cron_scheduler?.start_date}
                  >
                    Cancel Schedule
                  </Button>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Start Date</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.cron_scheduler?.start_date || null}
                  onChange={handleDateChange("start_date")}
                  disabled={!isEditable}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomFormLabel>End Date</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.cron_scheduler?.end_date || null}
                  onChange={handleDateChange("end_date")}
                  disabled={!isEditable}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomFormLabel>Time</CustomFormLabel>
                <CustomTimePicker
                  fullWidth
                  value={
                    values?.cron_scheduler?.hours &&
                    values?.cron_scheduler?.minutes
                      ? `${values.cron_scheduler.hours.padStart(
                          2,
                          "0",
                        )}:${values.cron_scheduler.minutes.padStart(2, "0")}:00`
                      : null
                  }
                  onChange={(timeString: any) => {
                    if (timeString) {
                      const [hours, minutes] = timeString.split(":");
                      setFieldValue("cron_scheduler.hours", hours);
                      // setFieldValue("cron_scheduler.minutes", minutes);
                      setFieldValue(
                        "cron_scheduler.minutes",
                        minutes.toString().replace(/^0+/, "") || "0",
                      );
                      setFieldValue("cron_scheduler.seconds", "0");
                    }
                  }}
                  disabled={!isEditable}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomFormLabel>Repeat</CustomFormLabel>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <ControlledCustomSelect
                      placeholder="Select Start and End Date"
                      value={values.cron_scheduler?.repeatType || "DAILY"}
                      onChange={handleRepeat}
                      options={getAvailableRepeatTypes(
                        values.cron_scheduler?.start_date || null,
                        values.cron_scheduler?.end_date || null,
                      ).map((option) => ({
                        label: capitalizeWords(option),
                        value: option,
                      }))}
                      fullWidth
                      disabled={
                        !isEditable ||
                        !values.cron_scheduler?.start_date ||
                        !values.cron_scheduler?.end_date
                      }
                    />
                  </Grid>
                </Grid>

                {values.cron_scheduler?.repeatType === "WEEKLY" && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <CustomFormLabel>Days of Week</CustomFormLabel>
                    <ControlledCustomSelect
                      multiple
                      value={selectedWeeks}
                      onChange={handleWeekChange}
                      options={daysOfWeek.map((day) => ({
                        label: day.label,
                        value: day.value.toString(),
                      }))}
                      renderValue={(selected) => (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4,
                          }}
                        >
                          {(selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={
                                daysOfWeek.find(
                                  (day) => day.value.toString() === value,
                                )?.label || value
                              }
                            />
                          ))}
                        </div>
                      )}
                      fullWidth
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Will repeat every selected day of the week
                    </Typography>
                  </Grid>
                )}

                {values.cron_scheduler?.repeatType === "DAILY" && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Will repeat every day
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveAndFinish}
                  sx={{
                    mt: 2,
                    ...(!isEditable && {
                      cursor: "not-allowed",
                    }),
                  }}
                  disabled={
                    !isEditable ||
                    !values.cron_scheduler?.start_date ||
                    !values.cron_scheduler?.end_date
                  }
                >
                  Save Schedule
                </Button>
                {marketCampaign.status === "SCHEDULED" && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleCancelSchedule}
                    sx={{
                      mt: 2,
                      ...(!isEditable && {
                        cursor: "not-allowed",
                      }),
                    }}
                    disabled={
                      !isEditable ||
                      !values.cron_scheduler?.start_date ||
                      !values.cron_scheduler?.end_date
                    }
                  >
                    Cancel Schedule
                  </Button>
                )}
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      )}

      {/* <Stack direction={"row"} justifyContent={"end"} mb={2}>
        <Button
          variant="contained"
          startIcon={<NavigateBefore />}
          onClick={handelBackButtonClick}
        >
          Back
        </Button>
      </Stack> */}
    </>
  );
};
